'use client';

import { MaterialSymbol, SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type TNoticeTone = 'info' | 'warning' | 'danger' | 'success' | 'brand' | 'neutral';

const TONES: Readonly<Record<TNoticeTone, { box: string; icon: SymbolCodepoints }>> = {
  info: { box: 'border-info-border bg-info-soft text-info-emphasis', icon: 'info' },
  warning: { box: 'border-warning-border bg-warning-soft text-warning-emphasis', icon: 'warning' },
  danger: { box: 'border-danger-border bg-danger-soft text-danger-emphasis', icon: 'error' },
  success: { box: 'border-success-border bg-success-soft text-success-emphasis', icon: 'check_circle' },
  brand: { box: 'border-brand-border bg-brand-soft text-brand-emphasis', icon: 'auto_awesome' },
  neutral: { box: 'border-border bg-muted text-foreground-muted', icon: 'info' },
};

const SIZES = {
  sm: { box: 'gap-2 px-3 py-2 text-xs', icon: 16 },
  md: { box: 'gap-3 p-4 text-sm', icon: 20 },
} as const;

interface INoticeProps {
  tone?: TNoticeTone;
  title?: ReactNode;
  children?: ReactNode;
  /** The tone's own icon by default; `null` drops it. */
  icon?: SymbolCodepoints | null;
  /** A button or link at the right end, e.g. "Resolver". */
  action?: ReactNode;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * The one notice box of the app (the dashboard's inline alert recipes): a tinted, bordered
 * line that tells something about the screen. Never a blank state (that is `NoDataFound`) and
 * never a transient "saved" confirmation (that is a toast); a panel that stays to say what to do
 * next after an action is fine. Danger and warning announce themselves.
 */
export function Notice({ tone = 'info', title, children, icon, action, size = 'md', className }: INoticeProps) {
  const resolvedIcon = icon === undefined ? TONES[tone].icon : icon;

  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex flex-wrap items-start rounded-lg border', SIZES[size].box, TONES[tone].box, className)}
    >
      {resolvedIcon !== null && (
        <MaterialSymbol icon={resolvedIcon} size={SIZES[size].icon} className="mt-px shrink-0" aria-hidden />
      )}
      {/* The text keeps a readable width; on a narrow screen the action drops below it. */}
      <div className="flex min-w-48 flex-1 flex-col gap-1">
        {title !== undefined && <span className="font-semibold">{title}</span>}
        {children}
      </div>
      {action !== undefined && <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
    </div>
  );
}
